import { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, DatePicker, MultiTagInput, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, cn } from "@nexus/ui";
import { SettingsField } from "../shared/SettingsRow";
import { FieldHelp } from "./FieldHelp";
import { KeyValueEditor } from "./KeyValueEditor";
import {
  ONBOARDING_FULL_ROW_CLASS,
  ONBOARDING_TAG_INPUT_CLASS,
  OnboardingCollapsibleSection,
  OnboardingSection,
} from "./OnboardingSection";
import {
  CLIENTS_PARAMS_FRONT_PRODUCT_COLUMNS,
  CLIENTS_PARAMS_TAGGING_SECTORS,
  type ClientsParamsFrontProductColumn,
  type ClientsParamsSetupColumn,
  type ClientsParamsTaggingSector,
} from "@nexus/contracts";
import {
  ATTRIBUTION_WINDOW_OPTIONS,
  CONVERSION_ACTION_OPTIONS,
  CUSTOMER_SETUP_CHECKBOX_COLUMNS,
  NC_COLUMNS_LEVEL_OPTIONS,
  normalizeMetaAccountIds,
  splitListValues,
  toCustomConversionAction,
  toCustomConversionId,
  toMetaAccountDigits,
  isNamingConventionColumn,
  type CustomerSetupCheckboxColumn,
  type CustomerSetupDraft,
  type CustomerSetupError,
  type CustomerSetupErrors,
  type CustomerSetupListColumn,
  type CustomerSetupTextColumn,
  toTsStartDraftDay,
  toTsStartPickerDate,
  tsStartDateBounds,
} from "@/features/identity-hub/lib/customer-setup";

const CONTROL_CLASS =
  "h-9 w-full rounded-md border bg-background px-3 text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50";

/** English labels. */
export const CUSTOMER_SETUP_LABEL_DEFAULTS: Record<ClientsParamsSetupColumn, string> = {
  account_id: "Meta ad account IDs",
  ts_start: "TS start",
  tagging_sector: "Tagging sector",
  position_nc: "Ad names follow a naming convention",
  position_nc_sep: "Separator",
  nc_names: "Part names, in order",
  nc_dict: "Code meanings",
  overwrite_conversion: "Overwrite conversion",
  overwrite_product: "Overwrite product",
  custom_pixel: "Custom pixel",
  low_memory_version: "Low memory version",
  attribution_window: "Attribution window",
  conversion_action: "Conversion action",
  adlib_url: "Ad Library URL",
  campaign_list: "Campaign list",
  google_account_ids: "Google Account IDs",
  google_custom_event: "Google Custom Event",
  google_custom_conversion: "Google Custom Conversion",
  front_product_column: "Platform product breakdown",
  nc_columns_level: "Naming Convention Columns Level",
  is_active: "Active",
  tiktok_advertiser_ids: "TikTok advertiser IDs",
};

const SELECT_TRIGGER_CLASS = "h-9 w-full text-[13px]";

/** Select value for "Custom conversion ID"; never stored. */
const CUSTOM_CONVERSION_OPTION = "__custom_conversion__";

const FRONT_PRODUCT_COLUMN_DEFAULTS: Record<ClientsParamsFrontProductColumn, string> = {
  Product_Code: "Product Code",
  Product_Type: "Product Type",
};

const SETUP_ERROR_DEFAULTS: Record<CustomerSetupError, string> = {
  required: "Required",
  accountIdFormat: "Each Meta account ID must be digits, for example 123456789",
  invalidTsStart: "Pick a date",
  tsStartOutOfRange: "Pick a date within the last 2 years, not in the future",
  incompleteMapping: "Fill in both the code and its meaning, or remove the row",
  duplicateMapping: "Each code can only be listed once",
  invalidUrl: "Enter an http(s) URL",
  customConversionId: "Enter the custom conversion ID as digits, for example 123456789",
  tooLong: "Too long. Shorten it and try again.",
  tooMany: "Too many values. Remove some and try again.",
  invalidValue: "Check this value.",
  invalid: "Check the customer setup values",
};

type Translate = (key: string, defaultValue: string) => string;

export function customerSetupLabel(t: Translate, column: ClientsParamsSetupColumn): string {
  return t(`iam.onboarding.setup.fields.${column}`, CUSTOMER_SETUP_LABEL_DEFAULTS[column]);
}

function customerSetupErrorMessage(t: Translate, error: CustomerSetupError): string {
  return t(`iam.onboarding.setup.errors.${error}`, SETUP_ERROR_DEFAULTS[error]);
}

interface CustomerSetupFieldsProps {
  draft: CustomerSetupDraft;
  errors: CustomerSetupErrors;
  onChange: (draft: CustomerSetupDraft) => void;
  disabled?: boolean;
  /** New organization passes `px-0`: its step body already has the side padding. */
  sectionClassName?: string;
}

/**
 * New Customer setup inputs (`run_management.clients_params`). Written once, never edited. The three
 * required inputs stay visible; the optional settings start collapsed.
 */
export function CustomerSetupFields({ draft, errors, onChange, disabled, sectionClassName }: CustomerSetupFieldsProps) {
  const { t } = useTranslation();
  const tsStartRange = tsStartDateBounds(new Date());
  const errorText = (error: CustomerSetupError | undefined) => (error ? customerSetupErrorMessage(t, error) : null);
  const attributionWindow = draft.texts.attribution_window;
  const isCustomAttributionWindow =
    attributionWindow.length > 0 && !(ATTRIBUTION_WINDOW_OPTIONS as readonly string[]).includes(attributionWindow);

  const hasAdvancedError = Boolean(
    errors.conversion_action ||
      errors.adlib_url ||
      errors.position_nc_sep ||
      errors.nc_names ||
      errors.nc_dict ||
      errors.custom_pixel ||
      errors.google_account_ids ||
      errors.google_custom_event ||
      errors.other,
  );

  return (
    <div data-testid="customer-setup-fields">
      <OnboardingSection
        className={sectionClassName}
        title={t("iam.onboarding.setup.title", "Data setup")}
        hint={t(
          "iam.onboarding.setup.createHint",
          "Set once when the customer is created. It can't be changed in Nexus afterwards.",
        )}
      >
        <SetupField label={customerSetupLabel(t, "account_id")} isRequired error={errorText(errors.account_id)}>
          {(id, errorId) => (
            <MultiTagInput
              className={ONBOARDING_TAG_INPUT_CLASS}
              id={id}
              aria-invalid={Boolean(errorId)}
              aria-describedby={errorId}
              disableDropdown
              disabled={disabled}
              value={draft.account_id}
              onChange={(value) => onChange({ ...draft, account_id: normalizeMetaAccountIds(value) })}
              renderTag={toMetaAccountDigits}
              placeholder={t("iam.onboarding.setup.accountIdPlaceholder", "123456789, then Enter")}
              addFreeTextLabel={(item) => t("iam.onboarding.listAdd", { item, defaultValue: "Add {{item}}" })}
            />
          )}
        </SetupField>

        <SetupField
          label={customerSetupLabel(t, "ts_start")}
          isRequired
          error={errorText(errors.ts_start)}
          hint={t(
            "iam.onboarding.setup.tsStartHint",
            "Up to 2 years back. Only the month is saved.",
          )}
        >
          {(id, errorId) => (
            <DatePicker
              id={id}
              value={toTsStartPickerDate(draft.ts_start)}
              onChange={(date) => onChange({ ...draft, ts_start: date ? toTsStartDraftDay(date) : "" })}
              fromDate={tsStartRange.min}
              toDate={tsStartRange.max}
              disabled={disabled}
              aria-invalid={Boolean(errors.ts_start)}
              aria-describedby={errorId}
              aria-required
              triggerClassName="text-[13px]"
              labels={{
                placeholder: t("iam.onboarding.setup.tsStartPlaceholder", "Select a date"),
                clear: t("iam.onboarding.setup.tsStartClear", "Clear"),
                today: t("iam.onboarding.setup.tsStartToday", "Today"),
              }}
            />
          )}
        </SetupField>

        <SetupField label={customerSetupLabel(t, "tagging_sector")} isRequired error={errorText(errors.tagging_sector)}>
          {(id, errorId) => (
            <Select
              value={draft.tagging_sector || undefined}
              onValueChange={(value) => onChange({ ...draft, tagging_sector: value as ClientsParamsTaggingSector })}
              disabled={disabled}
            >
              <SelectTrigger
                id={id}
                aria-invalid={errors.tagging_sector ? true : undefined}
                aria-describedby={errorId}
                className={cn(SELECT_TRIGGER_CLASS, errors.tagging_sector && "border-destructive")}
              >
                <SelectValue placeholder={t("iam.onboarding.setup.selectPlaceholder", "Select…")} />
              </SelectTrigger>
              <SelectContent>
                {CLIENTS_PARAMS_TAGGING_SECTORS.map((sector) => (
                  <SelectItem key={sector} value={sector}>
                    {sector}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </SetupField>
      </OnboardingSection>

      <OnboardingCollapsibleSection
        className={sectionClassName}
        data-testid="customer-setup-advanced"
        title={t("iam.onboarding.setup.advancedTitle", "More data settings")}
        summary={t("iam.onboarding.setup.advancedSummary", "Optional · conversions, Google, naming convention")}
        hasError={hasAdvancedError}
      >
        <SetupField label={customerSetupLabel(t, "front_product_column")}>
          {(id) => (
            <Select
              value={draft.front_product_column}
              onValueChange={(value) => onChange({ ...draft, front_product_column: value as ClientsParamsFrontProductColumn })}
              disabled={disabled}
            >
              <SelectTrigger id={id} className={SELECT_TRIGGER_CLASS}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CLIENTS_PARAMS_FRONT_PRODUCT_COLUMNS.map((column) => (
                  <SelectItem key={column} value={column}>
                    {t(`iam.onboarding.setup.frontProductColumn.${column}`, FRONT_PRODUCT_COLUMN_DEFAULTS[column])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </SetupField>

        <SetupListField
          column="custom_pixel"
          draft={draft}
          onChange={onChange}
          disabled={disabled}
          hint={t("iam.onboarding.setup.hints.custom_pixel", "Separate several values with commas.")}
          error={errorText(errors.custom_pixel)}
        />
        <SetupListField
          column="google_account_ids"
          draft={draft}
          onChange={onChange}
          disabled={disabled}
          error={errorText(errors.google_account_ids)}
        />
        <SetupListField
          column="google_custom_event"
          draft={draft}
          onChange={onChange}
          disabled={disabled}
          error={errorText(errors.google_custom_event)}
        />

        <SetupField
          label={customerSetupLabel(t, "attribution_window")}
          hint={
            isCustomAttributionWindow
              ? t("iam.onboarding.setup.attributionWindowCustomHint", "This value is not one of the standard options. It is kept unless you choose another.")
              : undefined
          }
        >
          {(id) => (
            <Select
              value={attributionWindow || undefined}
              onValueChange={(value) => onChange({ ...draft, texts: { ...draft.texts, attribution_window: value } })}
              disabled={disabled}
            >
              <SelectTrigger id={id} className={SELECT_TRIGGER_CLASS}>
                <SelectValue placeholder={t("iam.onboarding.setup.selectPlaceholder", "Select…")} />
              </SelectTrigger>
              <SelectContent>
                {ATTRIBUTION_WINDOW_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
                {isCustomAttributionWindow && (
                  <SelectItem value={attributionWindow}>
                    {t("iam.onboarding.setup.currentValue", { value: attributionWindow, defaultValue: "{{value}} (current value)" })}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
        </SetupField>

        <ConversionActionField
          draft={draft}
          onChange={onChange}
          disabled={disabled}
          error={errorText(errors.conversion_action)}
        />

        <SetupTextField column="adlib_url" draft={draft} onChange={onChange} disabled={disabled} error={errorText(errors.adlib_url)} />

        <div className={cn("flex flex-col gap-2", ONBOARDING_FULL_ROW_CLASS)}>
          {CUSTOMER_SETUP_CHECKBOX_COLUMNS.filter((column) => !isNamingConventionColumn(column)).map((column) => (
            <SetupCheckbox key={column} column={column} draft={draft} onChange={onChange} disabled={disabled} />
          ))}
        </div>

        <NamingConventionFields
          draft={draft}
          onChange={onChange}
          disabled={disabled}
          separatorError={errorText(errors.position_nc_sep)}
          namesError={errorText(errors.nc_names)}
          ncDictError={errorText(errors.nc_dict)}
        />

        {errors.other && <p className={cn("text-[11px] text-destructive", ONBOARDING_FULL_ROW_CLASS)}>{errorText(errors.other)}</p>}
      </OnboardingCollapsibleSection>
    </div>
  );
}

interface SetupControlProps {
  draft: CustomerSetupDraft;
  onChange: (draft: CustomerSetupDraft) => void;
  disabled?: boolean;
}

/**
 * The standard conversion actions, or a customer-specific `offsite_conversion.custom.<id>` whose
 * numeric ID is typed separately. Any other stored value stays selected as its own option.
 */
function ConversionActionField({ draft, onChange, disabled, error }: SetupControlProps & { error: string | null }) {
  const { t } = useTranslation();
  const value = draft.texts.conversion_action;
  const customId = toCustomConversionId(value);
  const isOtherValue =
    value.length > 0 && customId === null && !(CONVERSION_ACTION_OPTIONS as readonly string[]).includes(value);
  const setValue = (next: string) => onChange({ ...draft, texts: { ...draft.texts, conversion_action: next } });
  const customIdInputId = useId();

  return (
    <div className="flex flex-col gap-3">
      <SetupField label={customerSetupLabel(t, "conversion_action")}>
        {(id) => (
          <Select
            value={customId !== null ? CUSTOM_CONVERSION_OPTION : value || undefined}
            onValueChange={(next) => setValue(next === CUSTOM_CONVERSION_OPTION ? toCustomConversionAction(customId ?? "") : next)}
            disabled={disabled}
          >
            <SelectTrigger id={id} className={SELECT_TRIGGER_CLASS}>
              <SelectValue placeholder={t("iam.onboarding.setup.selectPlaceholder", "Select…")} />
            </SelectTrigger>
            <SelectContent>
              {CONVERSION_ACTION_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
              <SelectItem value={CUSTOM_CONVERSION_OPTION}>
                {t("iam.onboarding.setup.customConversionOption", "Custom conversion ID")}
              </SelectItem>
              {isOtherValue && (
                <SelectItem value={value}>
                  {t("iam.onboarding.setup.currentValue", { value, defaultValue: "{{value}} (current value)" })}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
      </SetupField>
      {customId !== null && (
        <SettingsField
          label={t("iam.onboarding.setup.customConversionIdLabel", "Custom conversion ID")}
          htmlFor={customIdInputId}
          required
          error={error}
          errorId={`${customIdInputId}-error`}
          hint={t("iam.onboarding.setup.customConversionIdHint", "Saved as offsite_conversion.custom.<ID>.")}
        >
          <input
            id={customIdInputId}
            type="text"
            inputMode="numeric"
            value={customId}
            onChange={(e) => setValue(toCustomConversionAction(e.target.value))}
            disabled={disabled}
            placeholder="123456789"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${customIdInputId}-error` : undefined}
            className={cn(CONTROL_CLASS, error ? "border-destructive" : "border-input-border")}
          />
        </SettingsField>
      )}
    </div>
  );
}

/**
 * The naming-convention columns in one section, with plain-language help and
 * an example. Keys and the draft shape are the same as everywhere else.
 */
function NamingConventionFields({
  draft,
  onChange,
  disabled,
  separatorError,
  namesError,
  ncDictError,
}: SetupControlProps & { separatorError: string | null; namesError: string | null; ncDictError: string | null }) {
  const { t } = useTranslation();
  const isNamingConventionOn = draft.checkboxes.position_nc;
  const title = t("iam.onboarding.setup.namingConvention.title", "Naming convention");
  return (
    <section
      className={cn("flex flex-col gap-4 rounded-lg border border-border-subtle p-4", ONBOARDING_FULL_ROW_CLASS)}
      data-testid="customer-setup-naming-convention"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <h5 className="text-xs font-semibold text-foreground">{title}</h5>
          <FieldHelp label={title}>
            {t(
              "iam.onboarding.setup.namingConvention.example",
              "Example: with separator _ and parts Country | Audience | Format, the ad name US_Prospecting_UGC is read as Country = US, Audience = Prospecting, Format = UGC.",
            )}
          </FieldHelp>
        </div>
        <p className="text-[11px] leading-snug text-muted-foreground">
          {t(
            "iam.onboarding.setup.namingConvention.hint",
            "How ad names are built, so Nexus can split each name into its parts.",
          )}
        </p>
      </div>
      <div className="@container">
        <div className="grid gap-x-6 gap-y-5 @md:grid-cols-2">
          <div className={ONBOARDING_FULL_ROW_CLASS}>
            <SetupCheckbox column="position_nc" draft={draft} onChange={onChange} disabled={disabled} />
          </div>
          {/* Separator and part names are only read, and only required, while the convention is on; their draft values stay put. */}
          {isNamingConventionOn && (
            <>
              <SetupTextField
                column="position_nc_sep"
                draft={draft}
                onChange={onChange}
                disabled={disabled}
                isRequired
                error={separatorError}
                hint={t("iam.onboarding.setup.namingConvention.help.position_nc_sep", "The character between parts. Default: _")}
              />
              <SetupListField
                column="nc_names"
                draft={draft}
                onChange={onChange}
                disabled={disabled}
                isRequired
                error={namesError}
                hint={t("iam.onboarding.setup.namingConvention.help.nc_names", "One name for each part of the ad name, in order.")}
              />
            </>
          )}
          <SetupOptionsField
            className={ONBOARDING_FULL_ROW_CLASS}
            column="nc_columns_level"
            options={NC_COLUMNS_LEVEL_OPTIONS}
            draft={draft}
            onChange={onChange}
            disabled={disabled}
          />
          <SetupField
            className={ONBOARDING_FULL_ROW_CLASS}
            label={customerSetupLabel(t, "nc_dict")}
            error={ncDictError}
            hint={t(
              "iam.onboarding.setup.namingConvention.help.nc_dict",
              "Optional. Turns short codes in ad names into readable meanings, such as UGC → User-generated content.",
            )}
          >
            {(id, errorId) => (
              <KeyValueEditor
                id={id}
                errorId={errorId}
                rows={draft.nc_dict}
                onChange={(rows) => onChange({ ...draft, nc_dict: rows })}
                keyLabel={t("iam.onboarding.setup.namingConvention.codeLabel", "Code")}
                valueLabel={t("iam.onboarding.setup.namingConvention.meaningLabel", "Meaning")}
                keyPlaceholder={t("iam.onboarding.setup.namingConvention.codePlaceholder", "UGC")}
                valuePlaceholder={t("iam.onboarding.setup.namingConvention.meaningPlaceholder", "User-generated content")}
                addLabel={t("iam.onboarding.setup.namingConvention.addMapping", "Add mapping")}
                hasError={Boolean(ncDictError)}
                disabled={disabled}
              />
            )}
          </SetupField>
        </div>
      </div>
    </section>
  );
}

function SetupListField({
  column,
  draft,
  onChange,
  disabled,
  hint,
  placeholder,
  isRequired,
  error,
}: SetupControlProps & {
  column: CustomerSetupListColumn;
  hint?: string;
  placeholder?: string;
  isRequired?: boolean;
  error?: string | null;
}) {
  const { t } = useTranslation();
  return (
    <SetupField label={customerSetupLabel(t, column)} hint={hint} isRequired={isRequired} error={error}>
      {(id, errorId) => (
        <MultiTagInput
          className={ONBOARDING_TAG_INPUT_CLASS}
          id={id}
          aria-invalid={Boolean(errorId)}
          aria-describedby={errorId}
          disableDropdown
          disabled={disabled}
          value={draft.lists[column]}
          onChange={(value) => onChange({ ...draft, lists: { ...draft.lists, [column]: splitListValues(value) } })}
          placeholder={placeholder ?? t("iam.onboarding.setup.listPlaceholder", "Type a value and press Enter")}
          addFreeTextLabel={(item) => t("iam.onboarding.listAdd", { item, defaultValue: "Add {{item}}" })}
        />
      )}
    </SetupField>
  );
}

/** Retool-style checkbox row for a list of known values. */
function SetupOptionsField({
  column,
  options,
  draft,
  onChange,
  disabled,
  className,
}: SetupControlProps & { column: CustomerSetupListColumn; options: readonly string[]; className?: string }) {
  const { t } = useTranslation();
  const selected = draft.lists[column];
  const toggle = (option: string, isChecked: boolean) => {
    const next = options.filter((item) => (item === option ? isChecked : selected.includes(item)));
    onChange({ ...draft, lists: { ...draft.lists, [column]: next } });
  };
  return (
    <fieldset className={cn("flex flex-col gap-1.5", className)} data-testid={`customer-setup-${column}`}>
      <legend className="mb-1.5 text-xs font-medium text-foreground">{customerSetupLabel(t, column)}</legend>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {options.map((option) => (
          <label key={option} className="flex items-center gap-2 text-xs text-foreground">
            <Checkbox
              checked={selected.includes(option)}
              disabled={disabled}
              onCheckedChange={(checked) => toggle(option, checked === true)}
            />
            {option}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function SetupTextField({
  column,
  draft,
  onChange,
  disabled,
  hint,
  error,
  isRequired,
}: SetupControlProps & { column: CustomerSetupTextColumn; hint?: string; error?: string | null; isRequired?: boolean }) {
  const { t } = useTranslation();
  return (
    <SetupField label={customerSetupLabel(t, column)} hint={hint} error={error} isRequired={isRequired}>
      {(id, errorId) => (
        <input
          id={id}
          type="text"
          value={draft.texts[column]}
          onChange={(e) => onChange({ ...draft, texts: { ...draft.texts, [column]: e.target.value } })}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          className={cn(CONTROL_CLASS, error ? "border-destructive" : "border-input-border")}
        />
      )}
    </SetupField>
  );
}

function SetupCheckbox({ column, draft, onChange, disabled }: SetupControlProps & { column: CustomerSetupCheckboxColumn }) {
  const { t } = useTranslation();
  return (
    <label className="flex items-center gap-2 text-xs text-foreground">
      <Checkbox
        checked={draft.checkboxes[column]}
        disabled={disabled}
        onCheckedChange={(checked) => onChange({ ...draft, checkboxes: { ...draft.checkboxes, [column]: checked === true } })}
      />
      {customerSetupLabel(t, column)}
    </label>
  );
}

/** The Organization tab's field: label, control, then the error or hint. */
function SetupField({
  label,
  isRequired,
  error,
  hint,
  className,
  children,
}: {
  label: string;
  isRequired?: boolean;
  error?: string | null;
  hint?: string;
  className?: string;
  /** `errorId` is set only while an error is shown, for the control's `aria-describedby`. */
  children: (id: string, errorId: string | undefined) => ReactNode;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <SettingsField label={label} htmlFor={id} required={isRequired} error={error} errorId={errorId} hint={hint} className={className}>
      {children(id, error ? errorId : undefined)}
    </SettingsField>
  );
}
